/**
 * NetworkSchematicView Component
 *
 * Full-screen schematic experience with controls and insights.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '../store';
import { NetworkData, NetworkSchematic } from './NetworkSchematic';
import DataBadge from './DataBadge';
import DataProvenanceRow from './DataProvenanceRow';

type LayoutMode = 'force' | 'hierarchical';
type LineWidthMode = 'fixed' | 'loading';

interface NetworkSchematicViewProps {
  data: NetworkData;
}

const formatMw = (value?: number | null, digits = 0) => {
  if (value === undefined || value === null || Number.isNaN(value)) return 'N/A';
  return `${value.toFixed(digits)} MW`;
};

const formatMvar = (value?: number | null, digits = 0) => {
  if (value === undefined || value === null || Number.isNaN(value)) return 'N/A';
  return `${value.toFixed(digits)} MVAr`;
};

const formatPercent = (value?: number | null, digits = 1) => {
  if (value === undefined || value === null || Number.isNaN(value)) return 'N/A';
  return `${value.toFixed(digits)}%`;
};

const formatMs = (value?: number | null) => {
  if (value === undefined || value === null || Number.isNaN(value)) return 'N/A';
  return `${value.toFixed(1)} ms`;
};

const NetworkSchematicView: React.FC<NetworkSchematicViewProps> = ({ data }) => {
  const { solverResult, solverConfig, gridSummary } = useStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const [layout, setLayout] = useState<LayoutMode>('hierarchical');
  const [showFlow, setShowFlow] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [highlightOverloads, setHighlightOverloads] = useState(true);
  const [lineWidthMode, setLineWidthMode] = useState<LineWidthMode>('loading');
  const [sizeByLoad, setSizeByLoad] = useState(true);
  const [showLegend, setShowLegend] = useState(true);
  const [showGenerators, setShowGenerators] = useState(true);
  const [showLoads, setShowLoads] = useState(true);

  const [selectedBusId, setSelectedBusId] = useState<number | undefined>(undefined);
  const [selectedBranchId, setSelectedBranchId] = useState<number | undefined>(undefined);

  const hasGenerators = data.generators.length > 0;
  const hasLoads = data.loads.length > 0;
  const hasBusLoad = data.buses.some((bus) => bus.p_mw !== undefined);

  useEffect(() => {
    if (!hasGenerators) setShowGenerators(false);
  }, [hasGenerators]);

  useEffect(() => {
    if (!hasLoads) setShowLoads(false);
  }, [hasLoads]);

  useEffect(() => {
    if (!hasBusLoad) setSizeByLoad(false);
  }, [hasBusLoad]);

  useEffect(() => {
    if (!containerRef.current) return;
    const element = containerRef.current;

    const updateSize = () => {
      setDimensions({
        width: element.clientWidth,
        height: element.clientHeight,
      });
    };

    updateSize();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }

    const observer = new ResizeObserver(() => updateSize());
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setSelectedBusId(undefined);
    setSelectedBranchId(undefined);
  }, [data]);

  const busLookup = useMemo(() => {
    const map = new Map<number, string>();
    data.buses.forEach((bus) => map.set(bus.id, bus.name));
    return map;
  }, [data.buses]);

  const selectedBus = useMemo(
    () => data.buses.find((bus) => bus.id === selectedBusId),
    [data.buses, selectedBusId]
  );
  const selectedBranch = useMemo(
    () => data.branches.find((branch) => branch.id === selectedBranchId),
    [data.branches, selectedBranchId]
  );

  const branchStress = useMemo(() => {
    const branches = [...data.branches];
    const topLoaded = branches
      .filter((branch) => branch.loading_percent !== undefined)
      .sort((a, b) => (b.loading_percent || 0) - (a.loading_percent || 0))
      .slice(0, 6);

    const topFlows = branches
      .filter((branch) => branch.p_from_mw !== undefined)
      .sort(
        (a, b) =>
          Math.abs(b.p_from_mw || 0) - Math.abs(a.p_from_mw || 0)
      )
      .slice(0, 6);

    const overloaded = branches.filter((branch) => (branch.loading_percent || 0) > 100).length;
    const highLoading = branches.filter((branch) => (branch.loading_percent || 0) > 80).length;
    const averageLoading = branches.length
      ? branches.reduce((sum, branch) => sum + (branch.loading_percent || 0), 0) / branches.length
      : null;

    return { topLoaded, topFlows, overloaded, highLoading, averageLoading };
  }, [data.branches]);

  const voltageStats = useMemo(() => {
    const buses = data.buses;
    const outliers = buses.filter(
      (bus) => bus.vm_pu !== undefined && (bus.vm_pu < 0.95 || bus.vm_pu > 1.05)
    );
    const underVoltage = outliers.filter((bus) => (bus.vm_pu || 1) < 0.95);
    const overVoltage = outliers.filter((bus) => (bus.vm_pu || 1) > 1.05);
    const lowest = [...buses]
      .filter((bus) => bus.vm_pu !== undefined)
      .sort((a, b) => (a.vm_pu || 0) - (b.vm_pu || 0))
      .slice(0, 5);
    const highest = [...buses]
      .filter((bus) => bus.vm_pu !== undefined)
      .sort((a, b) => (b.vm_pu || 0) - (a.vm_pu || 0))
      .slice(0, 5);

    return {
      outliers,
      underVoltage,
      overVoltage,
      lowest,
      highest,
    };
  }, [data.buses]);

  const topologyStats = useMemo(() => {
    const busCount = data.buses.length;
    const branchCount = data.branches.length;
    const avgDegree = busCount > 0 ? (2 * branchCount) / busCount : 0;
    return { busCount, branchCount, avgDegree };
  }, [data.buses.length, data.branches.length]);

  const solverStats = useMemo(() => {
    if (!solverResult) {
      return {
        converged: null,
        iterations: null,
        solveTime: null,
        maxMismatch: null,
        generation: null,
        load: null,
        losses: null,
        minVoltage: null,
        maxVoltage: null,
      };
    }

    return {
      converged: solverResult.converged,
      iterations: solverResult.iterations,
      solveTime: solverResult.solve_time_ms,
      maxMismatch: solverResult.max_mismatch,
      generation: solverResult.summary?.total_generation_mw,
      load: solverResult.summary?.total_load_mw,
      losses: solverResult.summary?.total_losses_mw,
      minVoltage: solverResult.summary?.min_voltage_pu,
      maxVoltage: solverResult.summary?.max_voltage_pu,
    };
  }, [solverResult]);

  const diagramWidth = Math.max(320, dimensions.width);
  const diagramHeight = Math.max(360, dimensions.height);

  if (!data.buses.length) {
    return (
      <div className="flex flex-col h-full items-center justify-center text-center text-gray-400">
        <div className="text-4xl mb-3">?</div>
        <h3 className="text-lg font-semibold text-white">No Network Data</h3>
        <p className="max-w-md text-sm">
          Run a power flow simulation to populate the schematic with live voltage and flow signals.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full gap-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-300">Network Schematic</div>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-semibold text-white">Operational One-Line</h2>
            <DataBadge tier="MODELLED" />
          </div>
          <p className="text-sm text-gray-400">
            Voltage posture, line stress, and flow direction in one coordinated view.
          </p>
          <DataProvenanceRow
            tier={solverResult?.provenance?.tier || 'MODELLED'}
            source={solverResult?.provenance?.source || 'NZ_GRID_SOLVER'}
            asOf={solverResult?.provenance?.as_of}
            generatedAt={solverResult?.provenance?.generated_at}
            refresh="manual (run solver)"
            inputs={
              solverResult?.provenance?.inputs || {
                solver_type: solverConfig.solver_type,
                load_factor: solverConfig.load_factor,
              }
            }
            warnings={solverResult?.provenance?.warnings || null}
            className="mt-2"
            compact
          />
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <StatusPill
            label="Solver"
            value={solverConfig.solver_type.toUpperCase()}
          />
          <StatusPill
            label="Load Factor"
            value={`${Math.round(solverConfig.load_factor * 100)}%`}
          />
          <StatusPill
            label="Convergence"
            value={solverStats.converged === null ? 'N/A' : solverStats.converged ? 'Converged' : 'Failed'}
            tone={solverStats.converged === null ? 'neutral' : solverStats.converged ? 'good' : 'bad'}
          />
          <StatusPill
            label="Solve Time"
            value={formatMs(solverStats.solveTime)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 2xl:grid-cols-[minmax(0,1fr)_360px] gap-4 flex-1 min-h-0">
        <div className="flex flex-col bg-gray-900 border border-gray-800 rounded-lg overflow-hidden min-h-[420px]">
          <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-gray-800 bg-gray-900/70">
            <ControlGroup label="Layout">
              <ToggleButton
                active={layout === 'force'}
                onClick={() => setLayout('force')}
                label="Organic"
              />
              <ToggleButton
                active={layout === 'hierarchical'}
                onClick={() => setLayout('hierarchical')}
                label="Stacked"
              />
            </ControlGroup>
            <ControlGroup label="Flow">
              <ToggleButton
                active={showFlow}
                onClick={() => setShowFlow((prev) => !prev)}
                label={showFlow ? 'On' : 'Off'}
              />
            </ControlGroup>
            <ControlGroup label="Labels">
              <ToggleButton
                active={showLabels}
                onClick={() => setShowLabels((prev) => !prev)}
                label={showLabels ? 'On' : 'Off'}
              />
            </ControlGroup>
            <ControlGroup label="Stress">
              <ToggleButton
                active={highlightOverloads}
                onClick={() => setHighlightOverloads((prev) => !prev)}
                label={highlightOverloads ? 'Alert' : 'Muted'}
              />
            </ControlGroup>
            <ControlGroup label="Line Width">
              <ToggleButton
                active={lineWidthMode === 'loading'}
                onClick={() => setLineWidthMode('loading')}
                label="Loading"
              />
              <ToggleButton
                active={lineWidthMode === 'fixed'}
                onClick={() => setLineWidthMode('fixed')}
                label="Fixed"
              />
            </ControlGroup>
            <ControlGroup label="Node Size">
              <ToggleButton
                active={sizeByLoad}
                onClick={() => setSizeByLoad((prev) => !prev)}
                label={sizeByLoad ? 'By Load' : 'By Degree'}
                disabled={!hasBusLoad}
                title={!hasBusLoad ? 'No bus load data available' : undefined}
              />
            </ControlGroup>
            <ControlGroup label="Icons">
              <ToggleButton
                active={showGenerators}
                onClick={() => setShowGenerators((prev) => !prev)}
                label="Gen"
                disabled={!hasGenerators}
                title={!hasGenerators ? 'No generator data available' : undefined}
              />
              <ToggleButton
                active={showLoads}
                onClick={() => setShowLoads((prev) => !prev)}
                label="Load"
                disabled={!hasLoads}
                title={!hasLoads ? 'No load data available' : undefined}
              />
            </ControlGroup>
            <ControlGroup label="Legend">
              <ToggleButton
                active={showLegend}
                onClick={() => setShowLegend((prev) => !prev)}
                label={showLegend ? 'On' : 'Off'}
              />
            </ControlGroup>
          </div>

          <div ref={containerRef} className="flex-1 min-h-[360px]">
            <NetworkSchematic
              data={data}
              width={diagramWidth}
              height={diagramHeight}
              layout={layout}
              showFlow={showFlow}
              showLabels={showLabels}
              showGenerators={showGenerators}
              showLoads={showLoads}
              showLegend={showLegend}
              highlightOverloads={highlightOverloads}
              lineWidthMode={lineWidthMode}
              sizeByLoad={sizeByLoad}
              selectedBusId={selectedBusId}
              selectedBranchId={selectedBranchId}
              onBusSelect={(bus) => {
                setSelectedBusId(bus?.id);
                setSelectedBranchId(undefined);
              }}
              onBranchSelect={(branch) => {
                setSelectedBranchId(branch?.id);
                setSelectedBusId(undefined);
              }}
            />
          </div>

          <div className="flex flex-wrap items-center gap-4 px-3 py-2 border-t border-gray-800 text-xs text-gray-400">
            <span>Buses: {topologyStats.busCount}</span>
            <span>Branches: {topologyStats.branchCount}</span>
            <span>Avg Degree: {topologyStats.avgDegree.toFixed(2)}</span>
            <span>Avg Loading: {formatPercent(branchStress.averageLoading)}</span>
          </div>
        </div>

        <div className="space-y-4 overflow-y-auto pr-1">
          <PanelCard title="System Snapshot">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <MetricRow label="Total Capacity" value={formatMw(gridSummary?.total_capacity_mw)} />
              <MetricRow label="Generation" value={formatMw(solverStats.generation)} />
              <MetricRow label="Total Load" value={formatMw(solverStats.load)} />
              <MetricRow label="Losses" value={formatMw(solverStats.losses, 1)} />
              <MetricRow label="Min Voltage" value={solverStats.minVoltage !== null ? `${solverStats.minVoltage?.toFixed(4)} pu` : 'N/A'} />
              <MetricRow label="Max Voltage" value={solverStats.maxVoltage !== null ? `${solverStats.maxVoltage?.toFixed(4)} pu` : 'N/A'} />
              <MetricRow label="Iterations" value={solverStats.iterations ?? 'N/A'} />
              <MetricRow label="Max Mismatch" value={solverStats.maxMismatch !== null ? solverStats.maxMismatch?.toExponential(2) : 'N/A'} />
            </div>
          </PanelCard>

          <PanelCard title="Stress Radar">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <MetricRow label="Overloaded Lines" value={branchStress.overloaded} tone={branchStress.overloaded > 0 ? 'bad' : 'good'} />
              <MetricRow label="High Loading" value={branchStress.highLoading} tone={branchStress.highLoading > 0 ? 'warn' : 'good'} />
              <MetricRow label="Under Voltage" value={voltageStats.underVoltage.length} tone={voltageStats.underVoltage.length > 0 ? 'bad' : 'good'} />
              <MetricRow label="Over Voltage" value={voltageStats.overVoltage.length} tone={voltageStats.overVoltage.length > 0 ? 'warn' : 'good'} />
            </div>
            <div className="mt-3 text-xs text-gray-400">
              Click any bus or line to see detailed metrics and flow context.
            </div>
          </PanelCard>

          <PanelCard title="Critical Lines">
            {branchStress.topLoaded.length === 0 ? (
              <div className="text-sm text-gray-400">No loading data available.</div>
            ) : (
              <div className="space-y-3">
                {branchStress.topLoaded.map((branch) => {
                  const loading = branch.loading_percent || 0;
                  const fromName = busLookup.get(branch.from_bus) || `Bus ${branch.from_bus}`;
                  const toName = busLookup.get(branch.to_bus) || `Bus ${branch.to_bus}`;
                  const label = branch.name || `${fromName} -> ${toName}`;
                  return (
                    <div key={branch.id} className="space-y-1">
                      <div className="flex justify-between text-xs text-gray-300">
                        <span className="truncate" title={label}>{label}</span>
                        <span className={loading > 100 ? 'text-red-400' : loading > 80 ? 'text-yellow-400' : 'text-green-400'}>
                          {formatPercent(loading)}
                        </span>
                      </div>
                      <div className="h-2 bg-gray-700 rounded">
                        <div
                          className={`h-full rounded ${loading > 100 ? 'bg-red-500' : loading > 80 ? 'bg-yellow-500' : 'bg-green-500'}`}
                          style={{ width: `${Math.min(100, loading)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </PanelCard>

          <PanelCard title="Voltage Outliers">
            {voltageStats.outliers.length === 0 ? (
              <div className="text-sm text-gray-400">All monitored buses are within 0.95-1.05 pu.</div>
            ) : (
              <div className="space-y-2 text-sm">
                {voltageStats.outliers.slice(0, 6).map((bus) => (
                  <div key={bus.id} className="flex justify-between">
                    <span className="text-gray-300">{bus.name}</span>
                    <span className={bus.vm_pu && bus.vm_pu < 0.95 ? 'text-red-400' : 'text-yellow-400'}>
                      {bus.vm_pu?.toFixed(4)} pu
                    </span>
                  </div>
                ))}
              </div>
            )}
          </PanelCard>

          <PanelCard title="Flow Leaders">
            {branchStress.topFlows.length === 0 ? (
              <div className="text-sm text-gray-400">No active flow results available.</div>
            ) : (
              <div className="space-y-2 text-sm">
                {branchStress.topFlows.map((branch) => {
                  const fromName = busLookup.get(branch.from_bus) || `Bus ${branch.from_bus}`;
                  const toName = busLookup.get(branch.to_bus) || `Bus ${branch.to_bus}`;
                  const p = branch.p_from_mw || 0;
                  const direction = p >= 0 ? `${fromName} -> ${toName}` : `${toName} -> ${fromName}`;
                  return (
                    <div key={branch.id} className="flex justify-between">
                      <span className="text-gray-300 truncate" title={direction}>{direction}</span>
                      <span className="text-blue-300">{Math.abs(p).toFixed(0)} MW</span>
                    </div>
                  );
                })}
              </div>
            )}
          </PanelCard>

          <PanelCard title="Selection Detail">
            {!selectedBus && !selectedBranch && (
              <div className="text-sm text-gray-400">
                Click a bus or line to inspect voltage, loading, and flow metrics.
              </div>
            )}
            {selectedBus && (
              <div className="space-y-2 text-sm">
                <div className="text-white font-semibold">{selectedBus.name}</div>
                <MetricRow label="Base KV" value={`${selectedBus.base_kv} kV`} />
                <MetricRow label="Voltage" value={selectedBus.vm_pu !== undefined ? `${selectedBus.vm_pu.toFixed(4)} pu` : 'N/A'} />
                <MetricRow label="Angle" value={selectedBus.va_deg !== undefined ? `${selectedBus.va_deg.toFixed(2)} deg` : 'N/A'} />
                <MetricRow label="P" value={formatMw(selectedBus.p_mw, 1)} />
                <MetricRow label="Q" value={formatMvar(selectedBus.q_mvar, 1)} />
              </div>
            )}
            {selectedBranch && (
              <div className="space-y-2 text-sm">
                <div className="text-white font-semibold">{selectedBranch.name || `Branch ${selectedBranch.id}`}</div>
                <MetricRow label="From" value={busLookup.get(selectedBranch.from_bus) || selectedBranch.from_bus} />
                <MetricRow label="To" value={busLookup.get(selectedBranch.to_bus) || selectedBranch.to_bus} />
                <MetricRow label="Flow" value={formatMw(selectedBranch.p_from_mw, 1)} />
                <MetricRow label="Loading" value={formatPercent(selectedBranch.loading_percent)} />
                <MetricRow label="Rate" value={selectedBranch.rate_mva ? `${selectedBranch.rate_mva.toFixed(0)} MVA` : 'N/A'} />
              </div>
            )}
          </PanelCard>

          <PanelCard title="Legend">
            <div className="grid grid-cols-2 gap-3 text-xs text-gray-300">
              <LegendRow color="bg-green-500" label="Voltage Normal" />
              <LegendRow color="bg-yellow-500" label="Voltage High" />
              <LegendRow color="bg-red-500" label="Voltage Low" />
              <LegendRow color="bg-blue-500" label="Flow Direction" />
              <LegendRow color="bg-gray-500" label="Line Normal" />
              <LegendRow color="bg-red-500" label="Line Overload" />
            </div>
            <div className="mt-3 text-xs text-gray-400">
              Node size reflects {sizeByLoad ? 'bus load' : 'topological degree'}.
            </div>
          </PanelCard>
        </div>
      </div>
    </div>
  );
};

const PanelCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-gray-800 rounded-lg p-4">
    <h3 className="text-sm font-semibold text-white mb-3">{title}</h3>
    {children}
  </div>
);

const MetricRow: React.FC<{ label: string; value: React.ReactNode; tone?: 'good' | 'warn' | 'bad' }> = ({
  label,
  value,
  tone,
}) => (
  <div className="flex justify-between gap-3">
    <span className="text-gray-400">{label}</span>
    <span
      className={
        tone === 'good'
          ? 'text-green-400'
          : tone === 'warn'
            ? 'text-yellow-400'
            : tone === 'bad'
              ? 'text-red-400'
              : 'text-white'
      }
    >
      {value}
    </span>
  </div>
);

const ToggleButton: React.FC<{
  active: boolean;
  onClick: () => void;
  label: string;
  disabled?: boolean;
  title?: string;
}> = ({ active, onClick, label, disabled, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
      active
        ? 'bg-blue-600 text-white'
        : 'bg-gray-800 text-gray-300 hover:text-white hover:bg-gray-700'
    } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
  >
    {label}
  </button>
);

const ControlGroup: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-center gap-2">
    <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span>
    <div className="flex items-center gap-1">{children}</div>
  </div>
);

const StatusPill: React.FC<{ label: string; value: string; tone?: 'good' | 'bad' | 'neutral' }> = ({
  label,
  value,
  tone = 'neutral',
}) => (
  <div
    className={`px-3 py-1 rounded-full text-xs border ${
      tone === 'good'
        ? 'border-green-500/40 text-green-300 bg-green-500/10'
        : tone === 'bad'
          ? 'border-red-500/40 text-red-300 bg-red-500/10'
          : 'border-gray-600 text-gray-200 bg-gray-800'
    }`}
  >
    <span className="text-gray-400 mr-1">{label}:</span>
    <span className="font-semibold">{value}</span>
  </div>
);

const LegendRow: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <div className="flex items-center gap-2">
    <div className={`h-3 w-3 rounded-full ${color}`} />
    <span>{label}</span>
  </div>
);

export default NetworkSchematicView;
